import { useTranslation } from 'module/hooks/use-translation';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { Row, Col } from 'antd';
import { isEmpty } from 'lodash';
import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  PageHeader,
  MenuItem,
  CollapsibleMenuGroup,
  MenuHeader,
  LeftMenu,
  LeftMenuContentWrapper,
} from './component';
import { Footer } from './styled';
import { MenuItem as MenuItemType } from './types';
import {
  getActiveMenuKeysForPath,
  getAllKeysFromMenuItems,
  getFilteredLeftMenuItems,
  isMenuGroup,
} from './utils';
import { Breadcrumb } from '../../atoms/breadcrumb';
import { Panel } from '../../atoms/collapse/panel';
import { FontAwesomeIcon } from '../../atoms/icon/icon';
import { SearchInputExpanded } from '../../atoms/input/search-input-expanded';
import { useSearchValueState } from '../../atoms/input/search-value-state';
import { MenuList } from '../../atoms/menu/menu-list';
import { Page } from '../../molecules/page';

export const KryptonMenuIcon = styled(FontAwesomeIcon).attrs({
  size: 20,
  isGradient: true,
})``;

const StyledRow = styled(Row)`
  overflow: hidden;
`;

interface Props {
  documentTitle: ReactNode;
  title: ReactNode;
  leftMenuItems: MenuItemType[];
  breadcrumbs?: ReactNode;
  footer?: ReactNode;
  content: ReactNode;
  showSearch?: boolean;
}

const ContentWrapper = styled(Col).attrs({ xxl: 20, xl: 19, lg: 19 })`
  display: flex;
`;
const onScrollAction = ({
  e,
  setIsScrolled,
}: {
  e: React.UIEvent<HTMLDivElement>;
  setIsScrolled: Dispatch<SetStateAction<boolean>>;
}) => {
  if ((e.target as HTMLDivElement)?.scrollTop > 0) {
    setIsScrolled(true);
  }
  if ((e.target as HTMLDivElement)?.scrollTop === 0) {
    setIsScrolled(false);
  }
};

const MenuItemComponent: FC<{
  item: MenuItemType;
  level: number;
  activeKeys: string[];
  setActiveKeys: (keys: string[]) => void;
  hideBorder?: boolean;
}> = ({ item, level, activeKeys, setActiveKeys, hideBorder = false }) => {
  const isLeaf = !isMenuGroup(item);
  const currentLevelToFormat = level === 1 ? 0 : level;

  if (isLeaf) {
    if (item.route == null) {
      return <MenuItem item={item} level={currentLevelToFormat} />;
    }

    return (
      <NavLink to={item.route} data-cy={item.key}>
        <MenuItem item={item} level={currentLevelToFormat} />
      </NavLink>
    );
  }

  return (
    <CollapsibleMenuGroup
      $level={level}
      $hideBorder={hideBorder}
      activeKey={activeKeys}
      onChange={(keys) => {
        if (item.collapsible !== false) {
          setActiveKeys(keys as string[]);
        }
      }}
      expandIconPosition="right"
      expandIcon={({ isActive }) => {
        if (item.dropdownIndicator != null) {
          return item.dropdownIndicator;
        }

        return isActive ? (
          <FontAwesomeIcon icon={faChevronUp} size={12} />
        ) : (
          <FontAwesomeIcon icon={faChevronDown} size={12} />
        );
      }}
    >
      <Panel
        key={item.key}
        header={
          level === 0 ? (
            <MenuHeader>{item.menuName}</MenuHeader>
          ) : (
            <MenuItem item={item} level={currentLevelToFormat} />
          )
        }
      >
        {item.subMenu.map((x) => (
          <MenuItemComponent
            key={x.key}
            item={x}
            level={level + 1}
            activeKeys={activeKeys}
            setActiveKeys={setActiveKeys}
          />
        ))}
      </Panel>
    </CollapsibleMenuGroup>
  );
};

export const Krypton: FC<Props> = ({
  documentTitle,
  title,
  leftMenuItems,
  breadcrumbs,
  footer,
  content,
  showSearch = true,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [collapseActiveKeys, setCollapseActiveKeys] = useState<string[]>([]);
  const searchPlaceholder = useTranslation('search');
  const {
    value: searchText,
    onChange: onSearchChange,
    clear: onSearchClear,
  } = useSearchValueState();
  const { pathname } = useLocation();
  const filteredLeftMenuItems = useMemo(
    () =>
      isEmpty(searchText)
        ? leftMenuItems
        : getFilteredLeftMenuItems(leftMenuItems, searchText),
    [leftMenuItems, searchText],
  );

  useEffect(() => {
    setCollapseActiveKeys(getAllKeysFromMenuItems(filteredLeftMenuItems));
  }, [filteredLeftMenuItems]);

  useEffect(() => {
    setCollapseActiveKeys((previous) =>
      getActiveMenuKeysForPath(filteredLeftMenuItems, pathname, previous),
    );
  }, [pathname, filteredLeftMenuItems]);

  return (
    <>
      <Helmet>{documentTitle}</Helmet>
      <Page $gray>
        <StyledRow>
          <LeftMenu
            onScrollCapture={(e) => {
              return onScrollAction({ e, setIsScrolled });
            }}
          >
            <PageHeader className={isScrolled ? 'heading-box-shadow' : ''}>
              {breadcrumbs && <Breadcrumb>{breadcrumbs}</Breadcrumb>}
              <h5>{title}</h5>
              {showSearch ? (
                <SearchInputExpanded
                  placeholder={searchPlaceholder}
                  value={searchText}
                  onChange={onSearchChange}
                  onClear={onSearchClear}
                />
              ) : null}
            </PageHeader>
            <LeftMenuContentWrapper>
              <MenuList>
                {filteredLeftMenuItems.map((menuItem, index) => {
                  return (
                    <li key={menuItem.key}>
                      <MenuItemComponent
                        item={menuItem}
                        level={0}
                        activeKeys={collapseActiveKeys}
                        setActiveKeys={setCollapseActiveKeys}
                        hideBorder={index === filteredLeftMenuItems.length - 1}
                      />
                    </li>
                  );
                })}
              </MenuList>
            </LeftMenuContentWrapper>
            {footer ? <Footer>{footer}</Footer> : null}
          </LeftMenu>
          <ContentWrapper xxl={20} xl={19} lg={19}>
            <Page.Content>{content}</Page.Content>
          </ContentWrapper>
        </StyledRow>
      </Page>
    </>
  );
};
